import { CliError, EXIT_INPUT } from "../../common.js";
import type { OperationalFailure } from "../../failures.js";
import { advanceSkippedEpisode, type RunContext } from "../../domain/ingest/video-context.js";
import type { VideoEpisodeDoc } from "../../domain/ingest/video-records.js";
import type { ScriptV4 } from "../../domain/script/v4/types.js";
import { WorkspaceStore } from "../../infra/ingest/workspace-store.js";
import { parseOperationalFailure } from "./failure.js";

export interface VideoSkipRecord {
  episode: number;
  skipped_at: string;
  reason: string;
  source_sha256: string;
  failure: OperationalFailure;
  attempts: OperationalFailure[];
}

export interface VideoSkipLedger {
  schema_version: 1;
  episodes: VideoSkipRecord[];
}

function invalidLedger(reason: string): CliError {
  return new CliError("The video skip ledger is invalid.", {
    exitCode: EXIT_INPUT,
    errorCode: "INGEST_SKIP_LEDGER_INVALID",
    hint: "Repair or remove skipped.json, then retry.",
    data: { actual: [reason] },
  });
}

function parseSkipRecord(value: unknown): VideoSkipRecord | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  if (!Number.isInteger(record["episode"]) || Number(record["episode"]) <= 0
      || typeof record["skipped_at"] !== "string"
      || typeof record["reason"] !== "string" || record["reason"].trim().length === 0
      || typeof record["source_sha256"] !== "string" || record["source_sha256"].length === 0
      || !Array.isArray(record["attempts"])) return null;
  const failure = parseOperationalFailure(record["failure"]);
  if (!failure) return null;
  const attempts: OperationalFailure[] = [];
  for (const value of record["attempts"]) {
    const attempt = parseOperationalFailure(value);
    if (!attempt) return null;
    attempts.push(attempt);
  }
  return {
    episode: Number(record["episode"]),
    skipped_at: record["skipped_at"],
    reason: record["reason"],
    source_sha256: record["source_sha256"],
    failure,
    attempts,
  };
}

export function readVideoSkipLedger(store: WorkspaceStore): VideoSkipLedger {
  if (!store.has("skipped.json")) return { schema_version: 1, episodes: [] };
  const value: unknown = store.readJson("skipped.json");
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw invalidLedger("root must be an object");
  const ledger = value as Record<string, unknown>;
  if (ledger["schema_version"] !== 1 || !Array.isArray(ledger["episodes"])) {
    throw invalidLedger("schema_version must be 1 and episodes must be an array");
  }
  const episodes: VideoSkipRecord[] = [];
  const seen = new Set<number>();
  for (const value of ledger["episodes"]) {
    const record = parseSkipRecord(value);
    if (!record) throw invalidLedger("one or more episode records are malformed");
    if (seen.has(record.episode)) throw invalidLedger(`episode ${record.episode} appears more than once`);
    seen.add(record.episode);
    episodes.push(record);
  }
  episodes.sort((left, right) => left.episode - right.episode);
  return { schema_version: 1, episodes };
}

export function writeVideoSkipLedger(store: WorkspaceStore, ledger: VideoSkipLedger): void {
  store.writeJson("skipped.json", ledger);
}

export function assertVideoSkipSources(
  skips: readonly VideoSkipRecord[],
  sourceEpisodes: ReadonlyArray<{ episode: number; sha256: string }>,
): void {
  const sourceByEpisode = new Map(sourceEpisodes.map((episode) => [episode.episode, episode.sha256]));
  for (const record of skips) {
    const sourceSha256 = sourceByEpisode.get(record.episode);
    if (sourceSha256 === record.source_sha256) continue;
    throw new CliError("The video skip ledger no longer matches the source.", {
      exitCode: EXIT_INPUT,
      errorCode: "INGEST_SKIP_SOURCE_MISMATCH",
      hint: "Restore the source episode recorded by this workspace, or rebuild the same source with --force.",
      data: {
        actual: [
          `episode=${record.episode}`,
          `ledger_sha256=${record.source_sha256}`,
          `source_sha256=${sourceSha256 ?? "<missing>"}`,
        ],
      },
    });
  }
}

export function skippedEpisodeDoc(episode: number): VideoEpisodeDoc {
  return {
    episode,
    synopsis: "",
    scenes: [],
    actors: [],
    locations: [],
    props: [],
    speakers: [],
    actorStates: [],
    locationStates: [],
    propStates: [],
  };
}

export function readAcceptedVideoDocs(
  store: WorkspaceStore,
  episodes: ReadonlyArray<{ episode: number }>,
  acceptedThrough: number,
  skips: readonly VideoSkipRecord[],
): VideoEpisodeDoc[] {
  const skipped = new Set(skips.map((record) => record.episode));
  const docs: VideoEpisodeDoc[] = [];
  for (const episode of episodes) {
    if (episode.episode > acceptedThrough) break;
    const rel = `ep/ep_${String(episode.episode).padStart(3, "0")}.json`;
    if (store.has(rel)) docs.push(store.readJson<VideoEpisodeDoc>(rel));
    else if (skipped.has(episode.episode)) docs.push(skippedEpisodeDoc(episode.episode));
  }
  return docs;
}

export function reconcileVideoSkips(
  store: WorkspaceStore,
  ctx: RunContext,
  skips: readonly VideoSkipRecord[],
): RunContext {
  for (const record of skips) {
    if (record.episode <= ctx.lastEpisode) {
      store.clearError(`ep/ep_${String(record.episode).padStart(3, "0")}.error.json`);
    }
  }
  const byEpisode = new Map(skips.map((record) => [record.episode, record]));
  for (;;) {
    const next = ctx.lastEpisode + 1;
    if (!byEpisode.has(next)) break;
    advanceSkippedEpisode(ctx, next);
    store.writeJson("context.json", ctx);
    store.clearError(`ep/ep_${String(next).padStart(3, "0")}.error.json`);
  }
  return ctx;
}

export function markSkippedEpisodes(script: ScriptV4, skips: readonly VideoSkipRecord[]): void {
  for (const record of skips) {
    const episode = script.episodes[record.episode - 1];
    if (!episode) continue;
    episode.extend = {
      scriptctl: {
        ingest: {
          outcome: "skipped",
          source_episode: record.episode,
        },
      },
    };
  }
}
